import { useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, MessageCircle, MapPin, Phone, Send } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { usePageContent, useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import useSeo from "@/lib/useSeo";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^\d{9}$/;

export default function Contacto() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Pedido de orçamento", message: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const { t } = usePageContent("contacto");
    const { tr, en } = useLocale();
    const contactEmail = useSetting("contact_email", "ola@novifood.pt");
    const contactPhone = useSetting("contact_phone", "+351 210 000 000");
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");
    const instagramHandle = useSetting("instagram_handle", "@novifood");
    const instagramUrl = useSetting("instagram_url", "https://instagram.com/novifood");
    const addressLine = useSetting("address_line", "Rua da Indústria, 123 — Porto, Portugal");
    const addressHours = useSetting("address_hours", tr("Visitas com marcação. Seg-Sex · 9h-18h", "Visits by appointment. Mon-Fri · 9am-6pm"), { localized: en });

    useSeo({
        title: tr("Contacto — Fale com a NOVIFOOD", "Contact — Talk to NOVIFOOD"),
        description: tr(
            "Pedidos de orçamento, parcerias com marcas e visitas ao showroom: contacte a NOVIFOOD por WhatsApp, email ou formulário. Resposta em menos de 6 horas.",
            "Quote requests, brand partnerships and showroom visits: contact NOVIFOOD via WhatsApp, email or form. Reply in under 6 hours."
        ),
        path: "/contacto",
    });

    const onChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            const digits = value.replace(/\D/g, "").slice(0, 9);
            setForm((f) => ({ ...f, phone: digits }));
            setErrors((er) => ({ ...er, phone: undefined }));
            return;
        }
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((er) => ({ ...er, [name]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (!form.name.trim()) er.name = tr("Nome obrigatório.", "Name is required.");
        if (!form.email.trim()) {
            er.email = tr("Email obrigatório.", "Email is required.");
        } else if (!EMAIL_REGEX.test(form.email.trim())) {
            er.email = tr("Email inválido (ex.: nome@dominio.pt).", "Invalid email (e.g. name@domain.com).");
        }
        if (form.phone && !PHONE_REGEX.test(form.phone)) {
            er.phone = tr("Telemóvel deve ter exactamente 9 dígitos.", "Phone must be exactly 9 digits.");
        }
        if (!form.message.trim()) er.message = tr("Mensagem obrigatória.", "Message is required.");
        setErrors(er);
        return Object.keys(er).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error(tr("Verifique os campos assinalados.", "Please check the highlighted fields."));
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/contact", form);
            toast.success(data.message || tr("Mensagem enviada!", "Message sent!"));
            setForm({ name: "", email: "", phone: "", subject: tr("Pedido de orçamento", "Quote request"), message: "" });
            setErrors({});
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg : typeof detail === "string" ? detail : tr("Erro ao enviar mensagem.", "Error sending message.");
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const phoneDigits = String(contactPhone).replace(/[^+\d]/g, "");

    return (
        <>
            <section className="relative overflow-hidden border-b border-border gradient-navy" data-testid="contact-hero">
                <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
                <div className="container-x relative hero-y text-white">
                    <span className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-secondary">
                        <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                        {t("hero", "eyebrow", tr("Falar com a NOVIFOOD", "Talk to NOVIFOOD"))}
                    </span>
                    <h1 className="mt-3 max-w-3xl display-xl">
                        {tr("Conte-nos sobre o seu ", "Tell us about your ")}<span className="serif-accent text-secondary">{tr("projecto", "project")}</span>.
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/80 leading-relaxed">
                        {t("hero", "subtitle", tr(
                            "Resposta em menos de 6 horas com uma primeira análise e reunião marcada.",
                            "Reply within 6 hours with a first analysis and a booked meeting."
                        ))}
                    </p>
                </div>
            </section>

            <section className="container-x section-y-sm" data-testid="contact-section">
                <div className="grid lg:grid-cols-12 gap-6 sm:gap-10">
                    <div className="lg:col-span-5 space-y-4">
                        <ContactTile icon={MessageCircle} title="WhatsApp" subtitle={`+${whatsappNumber}`} href={`https://wa.me/${whatsappNumber}`} testid="contact-whatsapp" />
                        <ContactTile icon={Mail} title="Email" subtitle={contactEmail} href={`mailto:${contactEmail}`} testid="contact-email" />
                        <ContactTile icon={Instagram} title="Instagram" subtitle={instagramHandle} href={instagramUrl} testid="contact-instagram" />
                        <ContactTile icon={Phone} title={tr("Telefone", "Phone")} subtitle={contactPhone} href={`tel:${phoneDigits}`} testid="contact-phone" />
                        <div className="rounded-xl border border-border bg-white p-6">
                            <div className="flex items-center gap-3">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                                    <MapPin className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-heading text-lg font-bold text-primary">{tr("Oficina & showroom", "Workshop & showroom")}</p>
                                    <p className="text-sm text-muted-foreground">{addressLine}</p>
                                </div>
                            </div>
                            <p className="mt-4 text-sm text-muted-foreground">{addressHours}</p>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <form onSubmit={submit} noValidate className="rounded-2xl border border-border bg-white p-6 lg:p-6 sm:p-8" data-testid="contact-form">
                            <h2 className="font-heading text-2xl lg:text-3xl font-extrabold text-primary">
                                {t("form", "title", tr("Envie-nos uma mensagem", "Send us a message"))}
                            </h2>
                            <p className="mt-1 text-sm text-muted-foreground">
                                {t("form", "subtitle", tr("Preencha o formulário e entraremos em contacto.", "Fill in the form and we'll get in touch."))}
                            </p>

                            <div className="mt-6 grid gap-5 sm:grid-cols-2">
                                <Field label={tr("Nome", "Name")} name="name" value={form.name} onChange={onChange} required error={errors.name} testid="contact-input-name" />
                                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required error={errors.email} testid="contact-input-email" />
                                <Field
                                    label={tr("Telemóvel (9 dígitos)", "Phone (9 digits)")}
                                    name="phone"
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="\d{9}"
                                    maxLength={9}
                                    placeholder="912345678"
                                    value={form.phone}
                                    onChange={onChange}
                                    error={errors.phone}
                                    testid="contact-input-phone"
                                />
                                <Field label={tr("Assunto", "Subject")} name="subject" value={form.subject} onChange={onChange} testid="contact-input-subject" />
                            </div>

                            <div className="mt-5">
                                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                    {tr("Mensagem", "Message")}<span className="ml-1 text-red-500">*</span>
                                </label>
                                <textarea
                                    name="message"
                                    rows={5}
                                    required
                                    value={form.message}
                                    onChange={onChange}
                                    aria-invalid={!!errors.message}
                                    data-testid="contact-input-message"
                                    className={cn(
                                        "mt-2 w-full rounded-md border bg-muted px-4 py-3 text-sm text-primary focus:bg-white focus:outline-none focus:ring-2",
                                        errors.message ? "border-red-500 focus:ring-red-500" : "border-border focus:ring-primary"
                                    )}
                                />
                                {errors.message && (
                                    <p className="mt-1 text-xs text-red-600" data-testid="contact-input-message-error">{errors.message}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="contact-submit-button"
                                className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition hover:-translate-y-0.5 disabled:opacity-60"
                            >
                                {loading ? tr("A enviar...", "Sending...") : t("form", "submit_label", tr("Enviar mensagem", "Send message"))} <Send className="h-4 w-4" />
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
}

function ContactTile({ icon: Icon, title, subtitle, href, testid }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            data-testid={testid}
            className="group flex items-center gap-4 rounded-xl border border-border bg-white p-5 transition hover:-translate-y-1 hover:shadow-lg"
        >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-secondary text-primary">
                <Icon className="h-5 w-5" />
            </div>
            <div>
                <p className="font-heading text-lg font-bold text-primary">{title}</p>
                <p className="text-sm text-muted-foreground group-hover:text-primary transition">{subtitle}</p>
            </div>
        </a>
    );
}

function Field({ label, name, value, onChange, type = "text", required, testid, error, placeholder, inputMode, pattern, maxLength }) {
    return (
        <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {label}{required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <input
                name={name}
                type={type}
                value={value}
                required={required}
                onChange={onChange}
                placeholder={placeholder}
                inputMode={inputMode}
                pattern={pattern}
                maxLength={maxLength}
                aria-invalid={!!error}
                data-testid={testid}
                className={cn(
                    "mt-2 w-full rounded-md border bg-muted px-4 py-3 text-sm text-primary focus:bg-white focus:outline-none focus:ring-2",
                    error
                        ? "border-red-500 focus:ring-red-500"
                        : "border-border focus:ring-primary"
                )}
            />
            {error && (
                <p className="mt-1 text-xs text-red-600" data-testid={`${testid}-error`}>{error}</p>
            )}
        </div>
    );
}
